/**
 * @author      Shivani Mehra
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  ToggleButtonGroup,
  ToggleButton,
  Overlay,
  Popover,
} from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";
import { useNavigate, useLocation } from "react-router-dom";
import { MultiSelect } from "react-multi-select-component";
import { saveAs } from "file-saver";
import CampaignTemplateModal from "./CampaignTemplateModal";
import { BsFilter } from "react-icons/bs";

const CampaignAdd = ({ selectedWhatsAppSetting, userInfo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingData = location.state || "";
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [allTemplateData, setAllTemplateData] = useState([]);
  const [show, setshow] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [campaignRecord, setCampaignRecord] = useState({
    name: "",
    description: "",
    start_date: "",
    type: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSpinner, setIsSpinner] = useState(false);
  const [tamplateparms, setTamplateParms] = useState({});
  const fileInputRef = useRef(null);
  const [optionGroups, setOptionGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);

  const instantCamp = userInfo?.modules?.some(
    (module) => module.url === "campaign_instant",
  );

  const hasGroupsModule =
    userInfo?.modules?.some((module) => module.url === "groups") || false;

  const [recurrenceType, setRecurrenceType] = useState("");
  const [customRecurrenceUnit, setCustomRecurrenceUnit] = useState("week");
  const [repeatEvery, setRepeatEvery] = useState(1);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [recurrenceEndType, setRecurrenceEndType] = useState("never");
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [templateRates, setTemplateRates] = useState([]);
  const [fileMembers, setFileMembers] = useState([]);
  const [finalTotalCost, setFinalTotalCost] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagFilterLogic, setTagFilterLogic] = useState("OR");
  const [filteredLeadOptions, setFilteredLeadOptions] = useState([]);
  const [showTagFilterPopover, setShowTagFilterPopover] = useState(false);
  const filterButtonRef = useRef(null);
  const prevTagsCountRef = useRef(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const isEditCloneMode = Boolean(
    existingData && existingData?.type === "clone",
  );
  const modeType = existingData?.type;
  const [existingFileInfo, setExistingFileInfo] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [enableReshoot, setEnableReshoot] = useState(false);
  const [reshootHours, setReshootHours] = useState(24);
  const [lineQuality, setLineQuality] = useState(null);

  const isQualityNotHigh = useMemo(() => {
    const q = (lineQuality || "").toLowerCase().trim();
    if (!q) return false;
    if (q === "high" || q === "green") return false;
    return true;
  }, [lineQuality]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await WhatsAppAPI.getWhatsAppSettingRecord();
        if (
          !res?.success ||
          !Array.isArray(res.record) ||
          !selectedWhatsAppSetting
        ) {
          if (!cancelled) setLineQuality(null);
          return;
        }
        const active = res.record.find(
          (s) => String(s.phone) === String(selectedWhatsAppSetting),
        );
        if (!cancelled) {
          setLineQuality(
            active?.quality != null ? String(active.quality) : null,
          );
        }
      } catch {
        if (!cancelled) setLineQuality(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedWhatsAppSetting]);

  // Fetch all available tags from API on component mount
  useEffect(() => {
    const fetchAllTags = async () => {
      try {
        const res = await WhatsAppAPI.getTags();
        if (res.success && Array.isArray(res.records)) {
          setAllTags(
            res.records.map((tag) => ({
              value: tag.id,
              label: tag.name,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    fetchAllTags();
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchAllTemplate(selectedWhatsAppSetting);
    fetchGroupRecords();
    fetchLeadsByTags([], "OR", true); // Initial fetch with no tags
    fetchtemplateRates();
  }, [selectedWhatsAppSetting]);

  // Add a new function to apply filters without losing selections
  const applyTagFilter = () => {
    const selectedTagIds = selectedTags.map((tag) => tag.value);
    fetchLeadsByTags(selectedTagIds, tagFilterLogic, false);
    setShowTagFilterPopover(false); // Close the popover
  };

  // Add a function to clear filters and reset to all leads
  const clearTagFilter = () => {
    setSelectedTags([]);
    setTagFilterLogic("OR");
    fetchLeadsByTags([], "OR", false);
  };
  // Fetch leads when tags or filter logic changes
  useEffect(() => {
    if (!isInitialLoad) {
      const selectedTagIds = selectedTags.map((tag) => tag.value);
      const clearedAllTags =
        prevTagsCountRef.current > 0 && selectedTagIds.length === 0;
      prevTagsCountRef.current = selectedTagIds.length;

      if (clearedAllTags) {
        setSelectedLeads([]);
      }
      fetchLeadsByTags(selectedTagIds, tagFilterLogic, false);
    }
    setIsInitialLoad(false);
  }, [selectedTags, tagFilterLogic]);

  // Clear form errors when fields are populated
  useEffect(() => {
    if (campaignRecord.name && formErrors.name) {
      setFormErrors((prev) => ({ ...prev, name: undefined }));
    }
    if (campaignRecord.start_date && formErrors.start_date) {
      setFormErrors((prev) => ({ ...prev, start_date: undefined }));
    }
  }, [
    campaignRecord.name,
    campaignRecord.start_date,
    formErrors.name,
    formErrors.start_date,
  ]);

  useEffect(() => {
    if (selectedTemplateName?.name && formErrors.template) {
      setFormErrors((prev) => ({ ...prev, template: undefined }));
    }
  }, [selectedTemplateName, formErrors.template]);

  useEffect(() => {
    if (recurrenceType === "custom") {
      if (
        customRecurrenceUnit === "week" &&
        selectedDays.length > 0 &&
        formErrors.recurrence
      ) {
        setFormErrors((prev) => ({ ...prev, recurrence: undefined }));
      }
      if (
        customRecurrenceUnit === "month" &&
        selectedDate &&
        formErrors.recurrence
      ) {
        setFormErrors((prev) => ({ ...prev, recurrence: undefined }));
      }
    }
    if (
      recurrenceEndType === "on" &&
      recurrenceEndDate &&
      formErrors.recurrenceEndDate
    ) {
      setFormErrors((prev) => ({ ...prev, recurrenceEndDate: undefined }));
    }
  }, [
    recurrenceType,
    customRecurrenceUnit,
    selectedDays,
    selectedDate,
    recurrenceEndType,
    recurrenceEndDate,
    formErrors.recurrence,
    formErrors.recurrenceEndDate,
  ]);

  const fetchtemplateRates = async () => {
    try {
      const templateRates = await WhatsAppAPI.templateRates();
      if (templateRates.success) {
        setTemplateRates(templateRates.records);
      } else {
        setTemplateRates([]);
      }
    } catch (error) {
      toast.error("Error fetching data.");
    }
  };

  // Handle edit/clone mode
  useEffect(() => {
    if (isEditCloneMode && existingData) {
      setCampaignRecord({
        name: existingData?.campaign_name
          ? `${existingData.campaign_name} clone`
          : "",
        description: existingData?.file_description || "",
        start_date: new Date(),
        type: existingData?.campaign_type || "Web",
      });

      if (existingData.body_text_params) {
        setTamplateParms(existingData.body_text_params);
      }

      setRecurrenceType(existingData?.recurrence || "");
      if (existingData?.custom_recurrence_unit) {
        setCustomRecurrenceUnit(existingData?.custom_recurrence_unit);
      }

      if (existingData?.selected_days) {
        try {
          if (typeof existingData?.selected_days === "string") {
            const daysString = existingData?.selected_days.replace(/[{}]/g, "");
            const days = daysString
              .split(",")
              .map((day) => day.replace(/"/g, "").trim());
            setSelectedDays(days);
          } else if (Array.isArray(existingData?.selected_days)) {
            setSelectedDays(existingData?.selected_days);
          }
        } catch (e) {
          console.error("Error parsing selected days:", e);
          setSelectedDays([]);
        }
      }

      if (existingData?.selected_date) {
        setSelectedDate(existingData?.selected_date);
      }

      if (existingData?.recurrence_end_type) {
        setRecurrenceEndType(existingData?.recurrence_end_type);
      }

      if (existingData?.recurrence_end_date) {
        const providedDate = moment(existingData?.recurrence_end_date);
        const tomorrow = moment().add(1, "days").startOf("day");

        if (providedDate.isSameOrAfter(tomorrow)) {
          setRecurrenceEndDate(providedDate.format("YYYY-MM-DD"));
        } else {
          setRecurrenceEndDate(tomorrow.format("YYYY-MM-DD"));
        }
      }

      if (existingData?.enable_reshoot !== undefined) {
        setEnableReshoot(existingData?.enable_reshoot);
      }

      if (existingData?.reshoot_hours !== undefined) {
        setReshootHours(existingData.reshoot_hours);
      }

      if (existingData?.file_id) {
        setExistingFileInfo({
          id: existingData?.file_id,
          title: existingData?.file_title,
          type: existingData?.file_type,
          size: existingData?.file_size,
          description: existingData?.file_description,
        });
      }

      if (existingData?.lead_ids?.length > 0) {
        const matchedLeads = leadOptions.filter((option) =>
          existingData.lead_ids.some(
            (lead) => lead.member_id === option.value.member_id,
          ),
        );
        setSelectedLeads(matchedLeads);
      }

      if (existingData?.groups?.length > 0) {
        const selectedGrp = existingData.groups.map((group) => ({
          value: group.id,
          label: group.name,
        }));
        setSelectedGroups(selectedGrp);
      }
    }
  }, [isEditCloneMode, existingData, allTemplateData, leadOptions]);

  const handleclose = () => {
    setshow(false);
    setSelectedTemplateName("");
    setTamplateParms({});
  };

  // Updated function to fetch leads based on tags (paginates until all rows loaded)
  const fetchLeadsByTags = async (
    tagIds = [],
    logic = "OR",
    shouldResetSelection = true,
  ) => {
    try {
      setIsLoadingLeads(true);
      const PAGE_SIZE = 2000;
      let offset = 0;
      let rawRecords = [];
      let fetchOk = false;

      const formatDisplayNumber = (whatsappNumber, countryCode = "") => {
        const shouldHideNumber =
          userInfo?.userrole !== "ADMIN" &&
          userInfo?.is_whatsapp_number_hidden === true;

        if (shouldHideNumber) {
          const num = whatsappNumber?.toString() || "";
          const maskedPart = "X".repeat(5);
          const visiblePart = num.slice(-5);
          return `${countryCode}${maskedPart}${visiblePart}`;
        }
        return `${countryCode}${whatsappNumber}`;
      };

      while (true) {
        const leadsResult = await WhatsAppAPI.getConversations({
          textName: "",
          recordType: "lead",
          limit: PAGE_SIZE,
          offset,
          tagIds: tagIds,
          tagFilterLogic: logic,
        });

        if (!leadsResult.success) {
          break;
        }
        fetchOk = true;

        const batch = leadsResult.records || [];
        rawRecords = rawRecords.concat(batch);

        const total = leadsResult.total ?? rawRecords.length;
        if (
          !leadsResult.hasMore ||
          batch.length === 0 ||
          rawRecords.length >= total
        ) {
          break;
        }
        offset += batch.length;
      }

      if (!fetchOk) {
        return;
      }

      const formattedLeadOptions = rawRecords.map((lead) => {
        const displayNumber = formatDisplayNumber(
          lead.full_number,
          lead.country_code ? lead.country_code : "",
        );

        return {
          label: `${lead.contactname} ${displayNumber}`,
          value: {
            member_id: lead.id,
            name: lead.contactname,
            whatsapp_number: `${lead.full_number}`,
            recordtypename: "lead",
            tags: (lead.tag_names || []).map((tag) => tag.id),
            tag_names: lead.tag_names || [],
          },
        };
      });

      setLeadOptions(formattedLeadOptions);
      setFilteredLeadOptions(formattedLeadOptions);

      if (tagIds.length > 0) {
        setSelectedLeads(formattedLeadOptions);
      } else if (shouldResetSelection) {
        setSelectedLeads([]);
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Error fetching leads data.");
    } finally {
      setIsLoadingLeads(false);
      setIsSpinner(true);
    }
  };

  // Fetch All templates
  const fetchAllTemplate = async (selectedWhatsAppSetting) => {
    try {
      const result = await WhatsAppAPI.getApprovedTemplates(
        selectedWhatsAppSetting,
      );

      if (result.error) {
        setAllTemplateData([]);
        setIsSpinner(true);
      } else {
        const transformedData = result?.data.map((row) => {
          const header =
            row.components.find((component) => component.type === "HEADER") ||
            {};
          const body =
            row.components.find((component) => component.type === "BODY") || {};
          const footer =
            row.components.find((component) => component.type === "FOOTER") ||
            {};
          const buttons =
            row.components.find((component) => component.type === "BUTTONS")
              ?.buttons || [];
          return {
            id: row.id,
            name: row.name,
            templatename: row.name.replace(/_/g, " "),
            language: row.language,
            status: row.status,
            category: row.category,
            header: header.format || "",
            header_text: header.format === "TEXT" ? header.text : "",
            header_image_url:
              header.format === "IMAGE"
                ? header.example?.header_handle?.[0] || ""
                : "",
            header_document_url:
              header.format === "DOCUMENT"
                ? header.example?.header_handle?.[0] || ""
                : "",
            header_video_url:
              header.format === "VIDEO"
                ? header.example?.header_handle?.[0] || ""
                : "",
            message_body: body.text || "",
            example_body_text: body.example?.body_text || [],
            add_security_recommendation:
              body.add_security_recommendation || false,
            code_expiration_minutes: footer.code_expiration_minutes || null,
            footer: footer.text || "",
            buttons: buttons
              .map((element) => {
                if (element.type === "OTP") {
                  return {
                    type: element.type,
                    otp_type: element.otp_type,
                    supported_apps:
                      element.supported_apps?.map((app) => ({
                        package_name: app.package_name,
                        signature_hash: app.signature_hash,
                      })) || [],
                  };
                } else {
                  return {
                    type: element.type,
                    text: element.text,
                    ...(element.type === "PHONE_NUMBER" && {
                      phone_number: element.phone_number,
                    }),
                    ...(element.type === "URL" && { url: element.url }),
                  };
                }
              })
              .filter((item) => item.text && item.type),
          };
        });

        const filteredTemplates = transformedData.filter(
          (template) => template.category !== "AUTHENTICATION",
        );
        setAllTemplateData(filteredTemplates);
        setFilteredTemplates(filteredTemplates);
        setIsSpinner(true);
      }
    } catch (error) {
      toast.error("Failed to fetch templates.");
    }
  };

  const fetchGroupRecords = async () => {
    const result = await WhatsAppAPI.fetchGroups(true);
    if (result.success) {
      const filteredGroups = result.records?.filter(
        (item) => item.members && item.members.length > 0 && item?.status,
      );
      const groupResult =
        filteredGroups?.map((item) => ({ value: item.id, label: item.name })) ||
        [];
      setOptionGroups(groupResult);
    } else {
      setOptionGroups([]);
    }
    setIsSpinner(true);
  };

  function generateOTP() {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  const handleGroupChange = (selectedOption) => {
    setSelectedGroups(selectedOption);
  };

  const handleLeadChange = (selectedOption) => {
    setSelectedLeads(selectedOption);
  };

  const handleChangeName = (event) => {
    const { name, value } = event.target;
    if (name === "start_date") {
      setCampaignRecord({
        ...campaignRecord,
        [name]: value ? moment(value).tz("Asia/Kolkata").toDate() : "",
      });
    } else {
      setCampaignRecord({ ...campaignRecord, [name]: value });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const validExtensions = ["csv"];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrorMessage("Only .csv files are allowed.");
        setSelectedFile(null);
        event.target.value = "";
        return;
      }

      const reader = new FileReader();

      reader.onload = (event) => {
        const content = event.target.result;
        const rows = content
          .split("\n")
          .map((row) => row.trim())
          .filter(Boolean);

        if (rows.length === 0) {
          setErrorMessage("The file is empty.");
          setSelectedFile(null);
          return;
        }

        const headers = rows[0]
          .split(",")
          .map((header) => header.trim().toLowerCase());
        const expectedHeaders = ["name", "countrycode", "number"];

        const isHeaderValid = expectedHeaders.every(
          (h, index) => headers[index] === h,
        );

        if (!isHeaderValid) {
          setErrorMessage(
            "File must contain headers in order: Name, Country Code, Number.",
          );
          setSelectedFile(null);
          event.target.value = "";
          return;
        }
        const members = rows.slice(1).map((row) => {
          const [name, countrycode, number] = row
            .split(",")
            .map((val) => val.trim());
          return { name, countrycode, number };
        });

        setFileMembers(members);
        setSelectedFile([file]);
        setExistingFileInfo(null);
        setErrorMessage("");
      };

      reader.readAsText(file);
    }
  };

  const calculateCampaignCost = async () => {
    const leadMembers = selectedLeads || [];
    const groupList = selectedGroups || [];
    const fileMemberList = fileMembers || [];
    const selectedCategory = selectedTemplateName?.category;

    if (!selectedCategory) {
      toast.error("Select template to calculate cost.");
      return 0;
    }

    if (
      leadMembers.length === 0 &&
      groupList.length === 0 &&
      fileMemberList.length === 0
    ) {
      toast.error(
        "Please select at least one member (lead, group, or file) to calculate cost.",
      );
      return 0;
    }

    try {
      let groupMembers = [];

      for (const group of groupList) {
        const response = await WhatsAppAPI.fetchGroupsById(group.value);
        const members = response?.records?.members || [];

        groupMembers.push(
          ...members.map((member) => ({
            country_code: member.country_code?.replace("+", "") || "91",
          })),
        );
      }

      const normalizedMembers = [
        ...leadMembers.map((lead) => ({
          country_code:
            lead.value?.whatsapp_number?.slice(0, 3)?.replace("+", "") || "91",
        })),
        ...fileMemberList.map((fileMember) => ({
          country_code: fileMember.countrycode || "91",
        })),
        ...groupMembers,
      ];

      let totalCost = 0;
      const countryCount = {};

      normalizedMembers.forEach((member) => {
        const code = member.country_code;
        countryCount[code] = (countryCount[code] || 0) + 1;

        const rateObj = templateRates.find((r) => r.country_code === code);
        if (rateObj) {
          const rate =
            selectedCategory === "MARKETING"
              ? parseFloat(rateObj.marketing)
              : parseFloat(rateObj.utility);
          totalCost += rate;
        }
      });
      const finalCost = parseFloat(totalCost.toFixed(4));
      setFinalTotalCost(finalCost);
      return finalCost;
    } catch (err) {
      toast.error("Failed to calculate cost.");
      return 0;
    }
  };

  function mapCarouselCards(cards) {
    return cards.map((card) => ({
      body: {
        text: card.body_text,
        example: card.body_text_example || [],
      },
      header: {
        format: card.header_type,
        handle:
          card.header_image_url ||
          card.header_video_url ||
          card.header_document_url ||
          "",
      },
      buttons: card.buttons.map((btn) => ({
        url: btn.url || "",
        text: btn.text,
        type: btn.type,
        example: btn.example || "",
        phone_number: btn.phone_number || "",
      })),
    }));
  }

  const handleEnableReshoot = (enabled) => {
    setEnableReshoot(enabled);
    if (!enabled) {
      setReshootHours(24);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isQualityNotHigh) {
      toast.error(
        "Your WhatsApp number quality is not high. You cannot create a campaign until quality is high again.",
      );
      return;
    }

    const errors = {};

    let totalCost = 0;

    if (!campaignRecord.name) {
      errors.name = "Campaign name is required.";
    }
    if (!instantCamp && !campaignRecord?.start_date) {
      errors.start_date = "Schedule date & time is required.";
    }
    if (!selectedTemplateName?.name) {
      errors.template = "Template selection is required.";
    }
    if (!instantCamp) {
      if (
        recurrenceType === "custom" &&
        customRecurrenceUnit === "week" &&
        selectedDays.length === 0
      ) {
        errors.recurrence =
          "Please select at least one day for weekly recurrence.";
      }

      if (
        recurrenceType === "custom" &&
        customRecurrenceUnit === "month" &&
        !selectedDate
      ) {
        errors.recurrence = "Please select a date for monthly recurrence.";
      }

      if (recurrenceEndType === "on" && !recurrenceEndDate) {
        errors.recurrenceEndDate =
          "Please select an end date for the recurrence.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const desc = campaignRecord?.description ? campaignRecord.description : "";
    if (selectedFile) {
      var formData = new FormData();
      for (let i = 0; i < selectedFile.length; i++) {
        formData.append(`selectedFile${i}`, selectedFile[i]);
        formData.append(`description`, desc);
      }
    }

    const groupIds = selectedGroups.map((group) => group.value);
    const leadIds = selectedLeads.map((lead) => lead.value);
    let formattedStartDate;
    let finalRecurrenceType = recurrenceType;

    if (instantCamp) {
      formattedStartDate = moment()
        .tz("Asia/Kolkata")
        .format("YYYY-MM-DDTHH:mm:ss.SSSZ");
      finalRecurrenceType = "";
    } else {
      formattedStartDate = campaignRecord.start_date
        ? moment(campaignRecord.start_date)
            .tz("Asia/Kolkata")
            .format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        : moment().tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm:ss.SSSZ");
    }

    const campiagnData = {
      name: campaignRecord.name,
      type: campaignRecord.type || "Web",
      status: "Pending",
      template_name: selectedTemplateName.name,
      template_id: selectedTemplateName.id,
      group_ids: groupIds,
      lead_ids: leadIds,
      business_number: selectedWhatsAppSetting,
      startDate: formattedStartDate,
      recurrence: instantCamp ? "" : finalRecurrenceType,
      custom_recurrence_unit:
        instantCamp || finalRecurrenceType !== "custom"
          ? null
          : customRecurrenceUnit,
      selected_days:
        instantCamp || customRecurrenceUnit !== "week" ? [] : selectedDays,
      selected_date:
        instantCamp || customRecurrenceUnit !== "month" ? null : selectedDate,
      recurrence_end_type: instantCamp ? "" : recurrenceEndType,
      recurrence_end_date:
        instantCamp || recurrenceEndType !== "on" || !recurrenceEndDate
          ? null
          : moment(recurrenceEndDate).format("YYYY-MM-DD"),
      enable_reshoot: enableReshoot,
      reshoot_hours: enableReshoot ? reshootHours : null,
    };

    try {
      if (userInfo?.has_wallet) {
        totalCost = await calculateCampaignCost();

        const tenantcode = userInfo.tenantcode;

        if (!tenantcode) {
          toast.error("Tenant code not found.");
          return;
        }

        const balanceCheckPayload = { amount: totalCost, tenantcode };
        const balanceRes = await WhatsAppAPI.checkBalance(balanceCheckPayload);
        if (!balanceRes.success || !balanceRes.sufficient) {
          setIsSpinner(true);
          setIsSending(true);
          toast.error(balanceRes.message || "Insufficient balance.");
          return;
        }
      }

      const isCarousel = selectedTemplateName.template_type === "carousel";

      const templateData = {
        template_id: selectedTemplateName.id,
        template_name: selectedTemplateName.name,
        language: selectedTemplateName.language,
        category: selectedTemplateName.category,
        message_body: selectedTemplateName.message_body || "",
        example_body_text: selectedTemplateName.example_body_text || "",
        business_number:
          selectedTemplateName.business_number || selectedWhatsAppSetting,
        ...(isCarousel
          ? {
              header: null,
              header_body: null,
              footer: null,
              buttons: null,
              cards: mapCarouselCards(selectedTemplateName.cards || null),
              template_type: "carousel",
            }
          : {
              header: selectedTemplateName.header || "TEXT",
              header_body: selectedTemplateName.header_body || "",
              footer: selectedTemplateName.footer || "",
              buttons: selectedTemplateName.buttons || [],
              cards: null,
              template_type: null,
            }),
      };

      const msgResult =
        await WhatsAppAPI.createMessageTemplateData(templateData);
      if (msgResult?.errors) {
        toast.error(msgResult?.errors);
        setIsSpinner(true);
        return;
      }

      const campaignResult =
        await WhatsAppAPI.insertCampaignRecords(campiagnData);
      if (campaignResult.success) {
        const cpId = campaignResult.record.id;

        if (tamplateparms && typeof tamplateparms === "object") {
          const {
            sendToAdmin,
            file,
            file_id,
            whatsapp_number_admin,
            ...restParameters
          } = tamplateparms;
          const isBodyTextParamsEmpty =
            Object.keys(restParameters).length === 0;
          const isFileIdEmpty = !file_id;
          const isWhatsappNumberAdminEmpty = !whatsapp_number_admin;

          if (
            !(
              isBodyTextParamsEmpty &&
              isFileIdEmpty &&
              isWhatsappNumberAdminEmpty
            )
          ) {
            const campaign_template_params = {
              campaign_id: cpId,
              body_text_params: restParameters,
              msg_history_id: null,
              file_id: file_id,
              whatsapp_number_admin: whatsapp_number_admin,
            };
            const campaignparamsResult =
              await WhatsAppAPI.insertCampaignParamsRecords(
                campaign_template_params,
              );
          }
        }

        let result = "";
        if (selectedFile) {
          result = await WhatsAppAPI.createCampaignFile(cpId, formData);
        } else if (existingFileInfo) {
          const fileClonePayload = {
            title: existingFileInfo.title,
            filetype: existingFileInfo.type,
            filesize: existingFileInfo.size,
            description: campaignRecord?.description || "",
            campaign_id: cpId,
          };

          result = await WhatsAppAPI.cloneCampaignFile(fileClonePayload);
        }

        if (campaignResult.success || result.success) {
          if (userInfo?.has_wallet) {
            const balanceRes = await WhatsAppAPI.createWalletTransaction({
              amount: totalCost,
              reason: `debited for sending WhatsApp campaign "${campiagnData?.name}"`,
              type: "debit",
              status: "paid",
              tenantcode: userInfo.tenantcode,
            });
            if (balanceRes.success) {
              // Optional: handle success
            }
          }
          toast.success("Record Inserted Successfully!");
          navigate("/campaign");
        }
        setIsSpinner(false);
      } else {
        toast.error(campaignResult.message || "Failed to create campaign.");
      }
    } catch (error) {
      console.error("Error submitting campaign:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSending(false);
      setIsSpinner(true);
    }
  };

  const handleClear = () => {
    setSelectedTemplateName("");
    setCampaignRecord({ name: "", description: "", start_date: "", type: "" });
    setSelectedFile(null);
    setIsSending(false);
    setEnableReshoot(false);
    setSelectedGroups([]);
    setSelectedLeads([]);
    setSelectedTags([]);
    setTagFilterLogic("OR");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatDateForInput = (date) => {
    return date ? moment(date).format("YYYY-MM-DDTHH:mm") : "";
  };

  const handleBack = () => {
    navigate("/campaign");
  };

  const csvData = [
    ["Name", "CountryCode", "Number"],
    ["John", "91", "XXXXXXXXXX"],
  ];

  const convertToCSV = (array) => {
    return array.map((row) => row.join(",")).join("\n");
  };

  const downloadCSV = () => {
    const csvString = convertToCSV(csvData);
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "contacts.csv");
  };

  const handleDayToggle = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const handleRemoveTag = (tagToRemove) => {
    setSelectedTags((prev) =>
      prev.filter((tag) => tag.value !== tagToRemove.value),
    );
  };

  const handleClearAllTags = () => {
    setSelectedTags([]);
  };

  return (
    <>
      <Container className="mt-5">
        <Row className="mx-5 text-center g-0">
          <Col lg={12} xs={12} sm={12}>
            <div
              className="p-2"
              style={{
                minHeight: "40px",
                backgroundColor: "#ffffff",
                borderRadius: "5px",
              }}
            >
              <div
                className="fw-semibold p-1"
                style={{ color: "#605C68", fontSize: "large" }}
                title={
                  isQualityNotHigh
                    ? "Submit is available only when WhatsApp number quality is high."
                    : undefined
                }
              >
                Add Campaign
              </div>
              {isQualityNotHigh && lineQuality && (
                <p
                  className="mb-0 small text-danger px-1 pt-1"
                  style={{ maxWidth: 720, margin: "0 auto" }}
                >
                  <strong>Your WhatsApp number quality is not high</strong> (
                  {String(lineQuality).toLowerCase()}). Submit is disabled
                  until Meta reports your quality as high again.
                </p>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {isSpinner ? (
        <>
          <Container className="mt-1 mb-5">
            <Row className="mx-5 g-0">
              <Col lg={12} sm={12} xs={12} className="mb-2">
                <Card className="h-100" style={{ border: "none" }}>
                  <Card.Body>
                    <Row className="mb-1">
                      <Col lg={instantCamp ? 12 : 6} sm={12} xs={12}>
                        <Form.Group
                          className="mb-3 mx-2"
                          controlId="formCampaignName"
                        >
                          <Form.Label>Campaign Name</Form.Label>
                          <Form.Control
                            style={{ height: "36px" }}
                            required
                            type="text"
                            name="name"
                            placeholder="Enter Campaign Name"
                            value={campaignRecord?.name}
                            onChange={handleChangeName}
                            isInvalid={!!formErrors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formErrors.name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      {!instantCamp && (
                        <Col lg={6} sm={12} xs={12}>
                          <Form.Group
                            className="mb-3 mx-2"
                            controlId="formStartDate"
                          >
                            <Form.Label>Schedule Date & Time</Form.Label>
                            <Form.Control
                              style={{ height: "36px" }}
                              required
                              type="datetime-local"
                              name="start_date"
                              value={formatDateForInput(
                                campaignRecord?.start_date,
                              )}
                              onChange={handleChangeName}
                              isInvalid={!!formErrors.start_date}
                            />
                            <Form.Control.Feedback type="invalid">
                              {formErrors.start_date}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                    <Row className="mb-1">
                      <Col lg={6} sm={12}>
                        <Form.Group
                          className="mb-3 mx-2"
                          controlId="formSelectLeads"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "8px",
                              flexWrap: "wrap",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              <Form.Label style={{ marginBottom: 0 }}>
                                Select Leads
                              </Form.Label>
                              <span
                                className="badge rounded-pill bg-primary"
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                }}
                              >
                                {selectedLeads.length} selected
                              </span>
                            </div>
                            <Button
                              className="btn-sm"
                              size="sm"
                              variant="outline-primary"
                              ref={filterButtonRef}
                              onClick={() =>
                                setShowTagFilterPopover(!showTagFilterPopover)
                              }
                              style={{
                                borderRadius: "5px",
                                minWidth: "80px",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <BsFilter style={{ marginRight: 2 }} /> Filter
                            </Button>
                          </div>

                          {selectedTags.length > 0 && (
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                marginBottom: "8px",
                                alignItems: "center",
                                overflowX: "auto",
                                overflowY: "hidden",
                                whiteSpace: "nowrap",
                                paddingBottom: "2px",
                                scrollbarWidth: "none",
                                msOverflowStyle: "none",
                              }}
                            >
                              {selectedTags.map((tag) => (
                                <span
                                  key={tag.value}
                                  className="badge bg-light text-dark border"
                                  style={{
                                    borderRadius: "0.375rem",
                                    padding: "4px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    backgroundColor: "#f0f0f0",
                                    border: "1px solid #ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {tag.label}
                                  <button
                                    type="button"
                                    className="btn-close btn-close-sm ms-1"
                                    style={{ fontSize: "0.5rem" }}
                                    onClick={() => handleRemoveTag(tag)}
                                  >
                                    <span className="visually-hidden">
                                      Remove
                                    </span>
                                  </button>
                                </span>
                              ))}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                style={{
                                  fontSize: "0.75rem",
                                  padding: "2px 6px",
                                  borderRadius: "0.375rem",
                                  whiteSpace: "nowrap",
                                }}
                                onClick={handleClearAllTags}
                                title="Clear all tags"
                              >
                                Clear All
                              </button>
                            </div>
                          )}

                          {isLoadingLeads && (
                            <div className="text-center text-muted small mb-2">
                              Loading leads...
                            </div>
                          )}

                          <MultiSelect
                            options={filteredLeadOptions}
                            value={selectedLeads}
                            onChange={handleLeadChange}
                            labelledBy="Select Leads"
                            disabled={isLoadingLeads}
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className="mb-3 mx-2">
                          <Form.Label htmlFor="formType">Type</Form.Label>
                          <Form.Select
                            className="w-100"
                            style={{ height: "42px" }}
                            aria-label="select type"
                            name="type"
                            value={campaignRecord?.type}
                            onChange={handleChangeName}
                          >
                            <option value="Web">Web</option>
                            <option value="Advertisement">Advertisement</option>
                            <option value="Banner Ads">Banner Ads</option>
                            <option value="Conference">Conference</option>
                            <option value="Direct Mail">Direct Mail</option>
                            <option value="Email">Email</option>
                            <option value="Partners">Partners</option>
                            <option value="Public Relations">
                              Public Relations
                            </option>
                            <option value="Other">Other</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-1">
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className="mb-3 mx-2" controlId="formFile">
                          <Form.Label>
                            {existingFileInfo ? (
                              <>
                                Existing file:&nbsp;
                                <a
                                  href={`/public/${userInfo.tenantcode}/campaign_files/${existingData.file_title}`}
                                  download={existingData.file_title}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {existingFileInfo.title} (
                                  {existingFileInfo.size} KB)
                                </a>
                              </>
                            ) : (
                              "File Upload"
                            )}
                          </Form.Label>

                          <Form.Control
                            type="file"
                            ref={fileInputRef}
                            accept=".csv"
                            onChange={handleFileChange}
                          />
                          {errorMessage && (
                            <Form.Text className="text-danger">
                              {errorMessage}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className="mb-3 mx-2">
                          <Form.Label htmlFor="formType">
                            <strong className="text-danger">*</strong> Select
                            Template
                          </Form.Label>
                          <Button
                            className="w-100 btn-group text-capitalize"
                            onClick={() => setshow(true)}
                            variant={
                              formErrors.template
                                ? "outline-danger"
                                : "outline-primary"
                            }
                          >
                            {selectedTemplateName?.name?.replace(/_/g, " ") ||
                              "Select..."}
                          </Button>
                          {formErrors.template && (
                            <Form.Text className="text-danger">
                              {formErrors.template}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="align-items-center">
                      {hasGroupsModule && (
                        <Col lg={6} sm={12} xs={12}>
                          <Form.Group
                            className="mb-3 mx-2"
                            controlId="groupSelect"
                          >
                            <Form.Label className="mb-3">
                              Select Groups
                            </Form.Label>
                            <MultiSelect
                              options={optionGroups}
                              value={selectedGroups}
                              onChange={handleGroupChange}
                              labelledBy="Select Groups"
                            />
                          </Form.Group>
                        </Col>
                      )}
                      <Col lg={6} sm={6} xs={6}>
                        <Form.Group
                          className="mb-3 mx-2"
                          controlId="formCampaignName mb-5"
                        >
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            style={{ height: "36px" }}
                            type="text"
                            name="description"
                            placeholder="type description here..."
                            value={campaignRecord?.description}
                            onChange={handleChangeName}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row
                      lg={12}
                      sm={12}
                      xs={12}
                      className="py-3 my-4 section-header"
                    >
                      RESHOOT TO FAILURES
                    </Row>
                    <Row className="mb-1">
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className="mb-3 mx-2">
                          <Form.Check
                            type="checkbox"
                            label="Automatically retry sending to numbers that failed on the first attempt"
                            checked={enableReshoot}
                            onChange={(e) =>
                              handleEnableReshoot(e.target.checked)
                            }
                          />
                          <Form.Text className="text-muted">
                            This reshoot will run after specified hours
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      {enableReshoot && (
                        <Col lg={3} sm={6} xs={6}>
                          <Form.Group className="mb-3 mx-2">
                            <Form.Label>Reshoot After (Hours)</Form.Label>
                            <Form.Control
                              style={{ height: "36px" }}
                              type="number"
                              min="2"
                              max="24"
                              value={reshootHours}
                              onChange={(e) =>
                                setReshootHours(
                                  Math.min(
                                    24,
                                    Math.max(2, parseInt(e.target.value) || 2),
                                  ),
                                )
                              }
                              placeholder="Enter hours (2-24)"
                            />
                            <Form.Text className="text-muted">
                              Minimum 2 hours, maximum 24 hours
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>

                    {!instantCamp && (
                      <>
                        <Row
                          lg={12}
                          sm={12}
                          xs={12}
                          className="py-3 my-4 section-header"
                        >
                          CUSTOM RECURRENCE
                        </Row>
                        <Row className="mb-1">
                          <Col lg={6} sm={12} xs={12}>
                            <Form.Group className="mb-3 mx-2">
                              <Form.Label>Repeat</Form.Label>
                              <Form.Select
                                style={{ height: "36px" }}
                                value={recurrenceType}
                                onChange={(e) =>
                                  setRecurrenceType(e.target.value)
                                }
                              >
                                <option value="">Does not repeat</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly </option>
                                <option value="fourthmonthly">
                                  Monthly on the fourth{" "}
                                  {moment().format("dddd")}{" "}
                                </option>
                                <option value="custom">Custom</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          {recurrenceType && recurrenceType !== "" && (
                            <>
                              <Col lg={2} sm={6} xs={6}>
                                <Form.Group className="mb-3 mx-2">
                                  <Form.Label>Repeat Ends</Form.Label>
                                  <ToggleButtonGroup
                                    type="radio"
                                    name="recurrence-end"
                                    value={recurrenceEndType}
                                    onChange={(val) =>
                                      setRecurrenceEndType(val)
                                    }
                                    className="d-flex"
                                  >
                                    <ToggleButton
                                      id="recurrence-end-never"
                                      value="never"
                                      variant={
                                        recurrenceEndType === "never"
                                          ? "primary"
                                          : "outline-primary"
                                      }
                                    >
                                      Never
                                    </ToggleButton>
                                    <ToggleButton
                                      id="recurrence-end-on"
                                      value="on"
                                      variant={
                                        recurrenceEndType === "on"
                                          ? "primary"
                                          : "outline-primary"
                                      }
                                    >
                                      On
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                </Form.Group>
                              </Col>
                              {recurrenceEndType === "on" && (
                                <Col lg={2} sm={6} xs={6}>
                                  <Form.Group className="mb-3 mx-2">
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control
                                      style={{ height: "36px" }}
                                      type="date"
                                      min={moment()
                                        .add(1, "days")
                                        .format("YYYY-MM-DD")}
                                      value={recurrenceEndDate}
                                      onChange={(e) =>
                                        setRecurrenceEndDate(e.target.value)
                                      }
                                    />
                                  </Form.Group>
                                </Col>
                              )}
                            </>
                          )}
                        </Row>
                        <Row className="mb-1">
                          {recurrenceType === "custom" && (
                            <>
                              <Col lg={6} sm={12} xs={12}>
                                <Form.Group className="mb-3 mx-2">
                                  <Form.Label>Repeat Every</Form.Label>
                                  <Form.Select
                                    style={{ height: "36px" }}
                                    value={customRecurrenceUnit}
                                    onChange={(e) =>
                                      setCustomRecurrenceUnit(e.target.value)
                                    }
                                  >
                                    <option value="week">Week(s)</option>
                                    <option value="month">Month(s)</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>

                              {customRecurrenceUnit === "week" && (
                                <Col lg={6} sm={12} xs={12}>
                                  <Form.Group className="mb-3 mx-2">
                                    <Form.Label className="mb-3">
                                      Repeat On (Days of Week)
                                    </Form.Label>
                                    <div className="d-flex flex-wrap">
                                      {[
                                        "Sunday",
                                        "Monday",
                                        "Tuesday",
                                        "Wednesday",
                                        "Thursday",
                                        "Friday",
                                        "Saturday",
                                      ].map((day) => (
                                        <Form.Check
                                          key={day}
                                          type="checkbox"
                                          label={day}
                                          value={day}
                                          checked={selectedDays.includes(day)}
                                          onChange={() => handleDayToggle(day)}
                                          className="me-3"
                                        />
                                      ))}
                                    </div>
                                  </Form.Group>
                                </Col>
                              )}

                              {customRecurrenceUnit === "month" && (
                                <Col lg={4} sm={12} xs={12}>
                                  <Form.Group className="mb-3 mx-2">
                                    <Form.Label>
                                      Repeat On (Date of Month)
                                    </Form.Label>
                                    <Form.Select
                                      style={{ height: "36px" }}
                                      value={selectedDate}
                                      onChange={(e) =>
                                        setSelectedDate(e.target.value)
                                      }
                                    >
                                      <option value="">Select Date</option>
                                      {[...Array(31)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                          {i + 1}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </Form.Group>
                                </Col>
                              )}
                            </>
                          )}
                        </Row>
                      </>
                    )}

                    <Row className="mt-3">
                      <Col lg={12} sm={12} xs={12}>
                        <hr></hr>
                      </Col>
                    </Row>
                    <Row className="g-0 mb-2">
                      <Col lg={3} sm={3} xs={3} className="text-start mt-2">
                        <Button
                          className="ms-2"
                          variant="outline-secondary"
                          onClick={downloadCSV}
                        >
                          <i className="fa fa-download me-2"></i>Download Sample
                          CSV
                        </Button>
                      </Col>
                      <Col lg={9} sm={9} xs={9} className="text-end mt-2">
                        {/* <b>
                          Approximate Total Cost{" "}
                          <span className="text-success">
                            {finalTotalCost}{" "}
                          </span>
                        </b> */}
                        {/* <Button
                          className="mx-2"
                          variant="light"
                          onClick={calculateCampaignCost}
                        >
                          Check Cost
                        </Button> */}
                        <Button
                          className="mx-2"
                          variant="light"
                          onClick={handleBack}
                          disabled={isSending}
                        >
                          Back
                        </Button>
                        <Button
                          className="mx-2"
                          variant="light"
                          onClick={handleClear}
                          disabled={isSending}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="outline-secondary"
                          disabled={isSending || isQualityNotHigh}
                          onClick={handleSubmit}
                          type="button"
                          title={
                            isQualityNotHigh
                              ? "Your WhatsApp number quality must be high to submit a campaign."
                              : undefined
                          }
                        >
                          {isSending ? "Submitting..." : "Submit"}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            {show && (
              <CampaignTemplateModal
                show={show}
                onHide={handleclose}
                setshow={setshow}
                setSelectedTemplateName={setSelectedTemplateName}
                selectedTemplateName={selectedTemplateName}
                setTemplateParms={setTamplateParms}
                tamplateparms={tamplateparms}
                selectedWhatsAppSetting={selectedWhatsAppSetting}
              />
            )}
          </Container>
        </>
      ) : (
        <div className="sk-cube-grid">
          <div className="sk-cube sk-cube1"></div>
          <div className="sk-cube sk-cube2"></div>
          <div className="sk-cube sk-cube3"></div>
          <div className="sk-cube sk-cube4"></div>
          <div className="sk-cube sk-cube5"></div>
          <div className="sk-cube sk-cube6"></div>
          <div className="sk-cube sk-cube7"></div>
          <div className="sk-cube sk-cube8"></div>
          <div className="sk-cube sk-cube9"></div>
        </div>
      )}

      <ToastContainer autoClose={30000} />

      <Overlay
        show={showTagFilterPopover}
        target={filterButtonRef.current}
        placement="bottom"
        containerPadding={20}
        rootClose
        onHide={() => setShowTagFilterPopover(false)}
        trigger="click"
      >
        <Popover id="tag-filter-popover" style={{ minWidth: 400 }}>
          <Popover.Header
            as="h3"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Filter Leads by Tags</span>
            <Button
              size="sm"
              variant="link"
              style={{ color: "#000", textDecoration: "none" }}
              onClick={() => setShowTagFilterPopover(false)}
            >
              X
            </Button>
          </Popover.Header>
          <Popover.Body style={{ minWidth: 400 }}>
            <div style={{ minWidth: 350, maxWidth: "100%" }}>
              <MultiSelect
                options={allTags}
                value={selectedTags}
                onChange={setSelectedTags}
                labelledBy="Select Tags"
                overrideStrings={{
                  selectSomeItems: "Select tags...",
                  allItemsAreSelected: "All tags selected",
                  selectAll: "Select All",
                  search: "Search tags",
                }}
                style={{ minWidth: 350, maxWidth: "100%" }}
              />
            </div>
            <div className="mt-3">
              <Form.Check
                inline
                label="OR"
                type="radio"
                checked={tagFilterLogic === "OR"}
                onChange={() => setTagFilterLogic("OR")}
              />
              <Form.Check
                inline
                label="AND"
                type="radio"
                checked={tagFilterLogic === "AND"}
                onChange={() => setTagFilterLogic("AND")}
              />
            </div>
            {selectedTags.length > 0 && (
              <div className="mt-3">
                <strong>Selected Tags:</strong>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  {selectedTags.map((tag) => (
                    <span
                      key={tag.value}
                      className="badge bg-light text-dark border"
                      style={{
                        borderRadius: "0.375rem",
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        backgroundColor: "#f0f0f0",
                        border: "1px solid #ccc",
                        display: "flex",
                        alignItems: "center",
                        marginRight: 4,
                      }}
                    >
                      {tag.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

export default CampaignAdd;
